import { useState } from 'react';
import { Helmet } from 'react-helmet';
import Layout from '@/components/Layout';
import { VehicleCard } from '@/components/VehicleCard';
import { VehicleFilters } from '@/components/VehicleFilters';
import { useRentalVehicles, RentalVehicleFilters } from '@/hooks/useRentalVehicles';
import { Loader2 } from 'lucide-react';
import carRentalHero from '@/assets/tours-hero-bg.jpg';

const CarRental = () => {
  const [filters, setFilters] = useState<RentalVehicleFilters>({});
  const { data: vehicles, isLoading } = useRentalVehicles(filters);

  const handleFiltersChange = (newFilters: RentalVehicleFilters) => {
    setFilters(newFilters);
  };

  return (
    <Layout>
      <Helmet>
        <title>Car Rental - Rent a Car in Dominican Republic</title>
        <meta
          name="description"
          content="Rent a car in Dominican Republic. Wide selection of economy, luxury, SUV, and van rentals at affordable prices. Book your vehicle today!"
        />
      </Helmet>

      {/* Hero Section */}
      <section
        className="relative min-h-[400px] flex items-center justify-center bg-cover bg-center"
        style={{ backgroundImage: `url(${carRentalHero})` }}
      >
        <div className="absolute inset-0 bg-black/50" />
        <div className="container mx-auto px-4 relative z-10 text-center text-white">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">Car Rental</h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto">
            Explore the Dominican Republic at your own pace with our premium vehicle fleet
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Filters Sidebar */}
            <aside className="lg:col-span-1">
              <div className="sticky top-24">
                <VehicleFilters onFiltersChange={handleFiltersChange} />
              </div>
            </aside>

            {/* Vehicles Grid */}
            <div className="lg:col-span-3">
              {isLoading ? (
                <div className="flex justify-center items-center min-h-[400px]">
                  <Loader2 className="w-8 h-8 animate-spin" />
                </div>
              ) : vehicles && vehicles.length > 0 ? (
                <>
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold">
                      Available Vehicles ({vehicles.length})
                    </h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {vehicles.map((vehicle) => (
                      <VehicleCard key={vehicle.id} vehicle={vehicle} />
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <p className="text-xl text-muted-foreground">
                    No vehicles found matching your criteria
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CarRental;